import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@rio-cloud/rio-uikit/Button';
import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';
import RadioButton from '@rio-cloud/rio-uikit/RadioButton';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Controller, type UseFormReturn, useFieldArray, useForm } from 'react-hook-form';

import EInvoicingFields from '@/components/EInvoicingFields';
import Input from '@/components/ui/Input';
import SelectInput from '@/components/ui/SelectInput';
import { FeatureToggles } from '@/configuration/featureToggle/featureToggles';
import { InvoiceDetailsSchema, type InvoiceDetailsSchemaType } from '@/features/administration/validations/invoice';
import { useCountryNames } from '@/hooks/useCountryNames';
import { useDisplayNames } from '@/hooks/useDisplayNames';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';
import { getVatCountries, t } from '@/lib/utils';
import { getAllowedCountries } from '@/stores/countryStore';
import { useUserStore } from '@/stores/userStore';

import { trackVatSubmission } from '../utils';

export type InvoiceDetailsProps = {
    invoiceData?: InvoiceDetailsSchemaType;
    saveChanges: (invoiceData: InvoiceDetailsSchemaType) => Promise<void>;
    handleCancel: () => void;
    loading: boolean;
    hasWritePermissions?: boolean;
};

const InvoiceDetailsComponent = forwardRef<UseFormReturn<InvoiceDetailsSchemaType>, InvoiceDetailsProps>(
    ({ invoiceData, saveChanges, handleCancel, loading, hasWritePermissions }, ref) => {
        const formatter = useDisplayNames();
        const { value: isAdditionVatEnabled } = useFeatureToggle(FeatureToggles.ENABLE_ADDITIONAL_VAT);
        const countryNames = useCountryNames;
        const countries = getAllowedCountries();

        const hasEInvoicing = Boolean(invoiceData?.schemeId && invoiceData?.participantIdentifier);
        const { eInvoicingDetails } = useUserStore();
        const hasEInvoicingSchemes = Boolean(eInvoicingDetails && eInvoicingDetails.length > 0);
        const canEditEInvoicing = !hasEInvoicing && hasEInvoicingSchemes;
        const showEInvoicing = hasEInvoicing || canEditEInvoicing;

        const [vatListOpen, setVatListOpen] = useState(false);

        const form = useForm<InvoiceDetailsSchemaType>({
            resolver: zodResolver(InvoiceDetailsSchema({ canEditEInvoicing })),
            defaultValues: invoiceData,
        });

        const {
            control,
            register,
            handleSubmit,
            watch,
            getValues,
            setValue,
            trigger,
            formState: { errors, isDirty },
            reset,
        } = form;

        const { vatList, vatCountry } = getValues();
        const { fields, append, remove } = useFieldArray({
            control,
            name: 'vatList',
        });

        useImperativeHandle(ref, () => form);

        const onSubmit = async (data: InvoiceDetailsSchemaType) => {
            const uniqueCountries = new Set(data.vatList.map(v => v.country));

            trackVatSubmission({
                totalVatNumbers: data.vatList?.length || 0,
                hasMultipleCountries: uniqueCountries.size > 1,
            });

            await saveChanges(data);
        };

        useEffect(() => {
            if (invoiceData) {
                reset(invoiceData);
            }
        }, [invoiceData, reset]);

        return (
            <div className='display-flex flex-1-1-0 flex-column margin-bottom-15 rounded bg-white'>
                <div className='display-flex justify-content-between'>
                    <div className='text-size-18 text-bold margin-bottom-15'>
                        {t('chargingContract.invoicingDetails.title')}
                    </div>
                </div>
                <div className='margin-bottom-15'>
                    <span>
                        {t(
                            hasWritePermissions
                                ? 'chargingContract.invoicingDetails.description'
                                : 'chargingContract.invoicingDetails.description.fleetUser'
                        )}
                    </span>
                </div>
                <Input
                    label={t('general.invoiceEmailAddress')}
                    required
                    error={errors?.email?.message}
                    inputAttributes={{ ...register('email'), disabled: loading || !hasWritePermissions }}
                />
                <div className='display-flex align-items-center margin-bottom-25'>
                    <span className='uikit-icon icon-info-sign' />
                    <i className='margin-left-5'>{t('general.beAwareThatAllInvoicesWillBeSent')}</i>
                </div>
                <div className='display-grid grid-cols-2 gap-15'>
                    {getValues('vatCountry') && formatter && (
                        <Input
                            label={t('general.domesticVATCountry')}
                            inputAttributes={{
                                disabled: true,
                                value: formatter(getValues('vatCountry')),
                            }}
                        />
                    )}
                    {getValues('vat') && (
                        <Input
                            label={t('general.domesticVATNumber')}
                            inputAttributes={{
                                disabled: true,
                                value: getValues('vat'),
                            }}
                        />
                    )}
                </div>
                {isAdditionVatEnabled && (
                    <div className='margin-top-10'>
                        <ExpanderPanel
                            open={vatListOpen}
                            onToggle={setVatListOpen}
                            title={`${t('chargingContract.additionalVATNumbers')} (${vatList?.length})`}
                            bsStyle={`${errors.vatList ? 'danger' : 'default'}`}
                            titleClassName='padding-10'
                            bodyClassName='bg-lightest padding-25'
                        >
                            <div className='gap-15 margin-0 display-flex flex-column padding-0'>
                                {fields.map((item, index) => (
                                    <div key={item.id} className='margin-bottom-25'>
                                        <div className='display-flex flex-wrap align-items-center gap-15'>
                                            <Controller
                                                key={item.id}
                                                name={`vatList.${index}.country`}
                                                control={control}
                                                render={({ field: { onChange, value } }) => (
                                                    <SelectInput
                                                        required
                                                        error={errors.vatList?.[index]?.country?.message}
                                                        label={t('general.country')}
                                                        className='min-width-150 margin-bottom-0'
                                                        selectAttributes={{
                                                            disabled: loading || !hasWritePermissions,
                                                            onChange: selectItem => onChange(selectItem?.id),
                                                            options: countryNames(
                                                                value,
                                                                getVatCountries(
                                                                    value ?? '',
                                                                    watch('vatList'),
                                                                    vatCountry ?? ''
                                                                )
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            />
                                            <Input
                                                required
                                                error={errors.vatList?.[index]?.vat?.message}
                                                inputAttributes={{
                                                    ...register(`vatList.${index}.vat`),
                                                    disabled: loading || !hasWritePermissions,
                                                }}
                                                className='flex-1-1-0 margin-bottom-0'
                                                label={t('general.VATNumber')}
                                            />
                                            {hasWritePermissions && (
                                                <button
                                                    className='btn uikit-icon icon-trash btn-icon-only text-size-20 text-color-dark margin-top-25 bg-none'
                                                    onClick={() => remove(index)}
                                                    type='button'
                                                />
                                            )}
                                        </div>
                                        <p className='margin-top-25 margin-bottom-5'>
                                            {t('general.doesTheCompanyHaveAPermanentEstablishment')}
                                        </p>
                                        <div className='gap-15 display-flex'>
                                            <Controller
                                                name={`vatList.${index}.permanentEstablishment`}
                                                control={control}
                                                render={({ field: { onChange, value } }) => (
                                                    <>
                                                        <RadioButton
                                                            checked={value === 'true'}
                                                            onChange={() => onChange('true')}
                                                            disabled={loading || !hasWritePermissions}
                                                        >
                                                            {t('general.yes')}
                                                        </RadioButton>
                                                        <RadioButton
                                                            checked={value === 'false'}
                                                            onChange={() => onChange('false')}
                                                            disabled={loading || !hasWritePermissions}
                                                        >
                                                            {t('general.no')}
                                                        </RadioButton>
                                                    </>
                                                )}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {hasWritePermissions &&
                                watch('vatList') &&
                                watch('vatList').length + 1 < countries.length && (
                                    <Button
                                        disabled={loading}
                                        onClick={() =>
                                            append(
                                                {
                                                    vat: '',
                                                    country: '',
                                                    permanentEstablishment: 'true',
                                                },
                                                { shouldFocus: false }
                                            )
                                        }
                                    >
                                        <span>{t('general.button.addAnotherVAT')}</span>
                                    </Button>
                                )}
                        </ExpanderPanel>
                    </div>
                )}
                {showEInvoicing && (
                    <div className='margin-top-25'>
                        {canEditEInvoicing ? (
                            <EInvoicingFields
                                control={control}
                                register={register}
                                watch={watch}
                                setValue={setValue}
                                trigger={trigger}
                                schemeIdName='schemeId'
                                participantIdentifierName='participantIdentifier'
                                vatName='vat'
                                schemeIdError={errors?.schemeId?.message}
                                participantIdentifierError={errors?.participantIdentifier?.message}
                                disabled={loading || !hasWritePermissions}
                                rowClassName='display-grid grid-cols-2 gap-15'
                            />
                        ) : (
                            <>
                                <label>{t('general.eInvoicingId')}</label>
                                <div className='display-grid grid-cols-2 gap-15'>
                                    <div className='display-flex align-items-end gap-5'>
                                        <Input
                                            className='flex-1-1-0'
                                            label={t('general.schemeId')}
                                            inputAttributes={{
                                                ...register('schemeId'),
                                                disabled: true,
                                            }}
                                        />
                                        <span style={{ marginBottom: '8px' }}>:</span>
                                        <Input
                                            className='flex-1-1-0'
                                            label={t('general.participantIdentifier')}
                                            inputAttributes={{
                                                ...register('participantIdentifier'),
                                                disabled: true,
                                            }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
                {hasWritePermissions && (
                    <div className='margin-top-25 display-flex flex-row justify-content-end gap-10'>
                        <Button disabled={!isDirty || loading} className='width-120' onClick={handleCancel}>
                            <span>{t('general.button.cancel')}</span>
                        </Button>
                        <Button
                            className={`width-120 ${loading ? 'btn-loading' : ''}`}
                            disabled={!isDirty || loading}
                            bsStyle='primary'
                            onClick={handleSubmit(onSubmit)}
                        >
                            <span>{t('general.button.saveChanges')}</span>
                        </Button>
                    </div>
                )}
            </div>
        );
    }
);

InvoiceDetailsComponent.displayName = 'InvoiceDetails';

export const InvoiceDetails = InvoiceDetailsComponent;
